import { ApiEnvelope, apiClient, authHeaders, renewSessionToken } from './apiClient';
import { LoginResponse, SessionUser } from 'model/userSession';
import { authClientKind } from './sessionActivity';

export type { ApiEnvelope };

/**
 * Auth / session / desktop profile. Mini apps use their own `api.ts` + `apiClient`.
 */
export class ApiService {
  async ping() {
    return apiClient.get<ApiEnvelope<string>>('/auth/ping', { timeout: 5000 });
  }

  async userLogin(username: string, password: string) {
    return apiClient.post<ApiEnvelope<LoginResponse>>('/auth/login', {
      username,
      password,
      client: authClientKind(),
    });
  }

  async logout() {
    return apiClient.post<ApiEnvelope<null>>('/auth/logout', {}, {
      ...authHeaders(),
      _skipAuthRetry: true,
    } as import('axios').InternalAxiosRequestConfig);
  }

  async renewSession() {
    return renewSessionToken();
  }

  async whoAmI() {
    return apiClient.get<ApiEnvelope<SessionUser>>('/auth/who-am-i', {
      ...authHeaders(),
      timeout: 8000,
      _skipAuthRetry: true,
    } as import('axios').InternalAxiosRequestConfig);
  }

  async setupHome(homePath: string) {
    return apiClient.post<ApiEnvelope<SessionUser>>('/auth/setup-home', { homePath }, authHeaders());
  }

  async saveUserAppPreferences(prefs: Record<string, Record<string, unknown>>) {
    return apiClient.put<ApiEnvelope<Record<string, Record<string, unknown>>>>(
      '/auth/user-app-preferences',
      prefs,
      authHeaders()
    );
  }

  async setWallpaper(path: string) {
    return apiClient.put<ApiEnvelope<null>>('/auth/wallpaper', { path }, { ...authHeaders(), timeout: 0 });
  }

  async fetchWallpaper() {
    const res = await apiClient.get<Blob>('/auth/wallpaper', {
      ...authHeaders(),
      responseType: 'blob',
      validateStatus: (s) => s === 200 || s === 204,
    });
    if (res.status !== 200 || !res.data || res.data.size <= 0) {
      return null;
    }
    return res.data;
  }

  async saveDisplayName(displayName: string | null) {
    return apiClient.put<ApiEnvelope<string | null>>('/auth/display-name', { displayName }, authHeaders());
  }

  async saveLocale(locale: string | null) {
    return apiClient.put<ApiEnvelope<string | null>>('/auth/locale', { locale }, authHeaders());
  }

  async saveTheme(theme: string | null) {
    return apiClient.put<ApiEnvelope<string | null>>('/auth/theme', { theme }, authHeaders());
  }
}

export const apiService = new ApiService();
export default apiService;
